import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthenticationResult } from '@azure/msal-browser';
import { StorageService } from '@core/services/storage/storage.service';
import { Client, ClientOptions } from '@microsoft/microsoft-graph-client';
import { GDriveFolder } from '@shared/models/file-types';

import { TranslateService } from '@ngx-translate/core';
import { SOCIAL_URL } from '../constants/app-constant';
import { ADD_FILE_OPTION_NAME } from '../constants/app.enum';
import { STORAGE_CONST } from '../constants/storage-constants';

@Injectable()
export class Onedrive {
  accessToken: any;
  folderHistory: GDriveFolder[] = [
    {
      name: this.translateService.instant('ALL_FILES'),
      id: 'root',
      drive: '',
    },
  ];
  appKey: any;
  redirectURI: any;
  url: any;
  client_id: string;
  client_secret: string;
  scope: string;
  client: Client;

  private service: ADD_FILE_OPTION_NAME;

  constructor(
    private httpClient: HttpClient, // public iab: InAppBrowser
    private storageSvc: StorageService,
    private translateService: TranslateService
  ) {}

  initClient() {
    const clientOptions: ClientOptions = {
      debugLogging: true,
      authProvider: {
        getAccessToken: async () => this.accessToken,
      },
    };
    this.client = Client.initWithMiddleware(clientOptions);
  }

  setAccessToken(token) {
    this.accessToken = token;
  }

  async setService(svc: ADD_FILE_OPTION_NAME) {
    this.service = svc;
    await this.storageSvc.set(STORAGE_CONST.SERVICE, this.service);
  }

  async getService(): Promise<string> {
    if (!this.service) {
      this.service = await this.storageSvc.get(STORAGE_CONST.SERVICE);
    }
    return this.service;
  }

  saveToken(token: string) {
    const data = {
      access_token: token,
      expires: this.currentTimestamp() + 1000000,
    };

    // const params = JSON.parse('{"' + decodeURI(url).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g,'":"') + '"}');
    // params.expires = parseInt(params.expires_in?params.expires_in:'0', 10) + this.currentTimestamp();
    // this.storageSvc.setJson('onedrive', params);
    // return params.access_token;
    this.storageSvc.setJson(STORAGE_CONST.ONE_DRIVE, data);
  }

  async getAccount(): Promise<AuthenticationResult> {
    await this.getService();
    const obj = await this.storageSvc.getJson(this.service.replace(/ /g, '').toLowerCase());
    if (obj) {
      if (obj.expires > this.currentTimestamp()) {
        const result = obj.auth as AuthenticationResult;
        this.accessToken = result.accessToken;
        return result;
      }
    }

    return null;
  }

  async saveAccount(account: AuthenticationResult) {
    const data = {
      auth: account,
      expires: this.currentTimestamp() + 1000000,
    };
    this.accessToken = account.accessToken;
    await this.storageSvc.setJson(this.service.replace(/ /g, '').toLowerCase(), data);
  }

  async getToken() {
    const obj = await this.storageSvc.getJson(STORAGE_CONST.ONE_DRIVE);
    if (obj) {
      if (obj.expires > this.currentTimestamp()) {
        return obj.access_token;
      }
    }

    return null;
  }

  async downloadFile(fileId: string): Promise<Blob> {
    return new Promise<any>(async (resolve, reject) => {
      const url = await this.client
        // .api(`/me/drive/items/${fileId}?select=id,@microsoft.graph.downloadUrl`)
        .api(SOCIAL_URL.microsoftFileDownLoad(fileId))
        .get();

      const downloadUrl = url['@microsoft.graph.downloadUrl'];

      this.httpClient
        .get(downloadUrl, {
          responseType: 'blob', // <-- changed to blob
        })
        .subscribe({
          next: (res) => {
            resolve(res);
          },
          error: (error) => reject(error),
        });
      return;
    });
  }

  getSubFolder(folder: GDriveFolder) {
    this.pushToHistory(folder);

    return (
      this.client
        // .api(`/drives/${folder.drive}/items/${folder.id}/children`)
        .api(SOCIAL_URL.microsoftSubFolder(folder.drive, folder.id))
        .get()
    );
  }

  pushToHistory(folder: GDriveFolder) {
    const history = this.folderHistory;

    const i = history.findIndex((e) => e.id === folder.id);

    if (i >= 0) {
      this.folderHistory = history.slice(0, i);
    }

    this.folderHistory.push(folder);
  }

  async getFolders(folder: GDriveFolder): Promise<any> {
    if (!this.client) {
      this.initClient();
    }

    this.pushToHistory(folder);
    if (this.client) {
      // return await this.client.api("/me/drive/root/children").get();
      return await this.client.api(SOCIAL_URL.microsoftClientFolder).get();
    }

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'bearer ' + this.accessToken,
      }),
    };

    // let folderPath;
    //
    // if (typeof path == 'undefined' || !path) {
    //   folderPath = {
    //     path: '',
    //   };
    // } else {
    //   folderPath = {
    //     path,
    //   };
    //
    //   if (this.folderHistory[this.folderHistory.length - 1] !== path) {
    //     this.folderHistory.push(path);
    //   }
    // }

    return await this.httpClient
      .get(
        // `https://graph.microsoft.com/v1.0/me/drive/${folder}/children`,
        SOCIAL_URL.microsoftFolder(folder.id),
        httpOptions
      )
      .toPromise();
  }
  getEmails(next: string = null): Promise<any> {
    if (!this.client) {
      this.initClient();
    }

    const totalRecord = 20;
    if (this.client) {
      if (next && this.isNumber(next)) {
        const skip = (Number(next || -1) + 1) * totalRecord;
        return this.client.api(SOCIAL_URL.microsoftEmail).skip(skip).top(totalRecord).get();
      } else if (next) {
        return this.client.api(next.split('v1.0')[1]).get();
      }
      return this.client.api(SOCIAL_URL.microsoftEmail).top(totalRecord).get();
    }
    return null;
  }
  isNumber(value: any) {
    try {
      return !isNaN(Number(value));
    } catch (error) {
      return false;
    }
  }
  searchEmails(search, next: string = null): Promise<any> {   
    if (search) {
      if (next) {
        return this.client.api(next.split('v1.0')[1]).get();
      }
      // return this.client.api(`/me/messages?$search="${search}"`).get();
      return this.client.api(SOCIAL_URL.microsoftEmailSearch(search)).get();
    } else {
      return this.getEmails();
    }
  }

  getHotmailAttachmentData(userId, id) {
    // return this.client.api(userId + '/messages/' + id + '/attachments').get();
    return this.client.api(SOCIAL_URL.hotMailAttachment(userId, id)).get();
  }

  downloadHotmailAttachment(userId, messageId, attachmentId) {
    return (
      this.client
        // .api(userId + '/messages/' + messageId + '/attachments/' + attachmentId)
        .api(SOCIAL_URL.downloadHotMailAttachment(userId, messageId, attachmentId))
        .get()
    );
  }

  private currentTimestamp(): number {
    return Math.floor(new Date().getTime() / 1000);
  }
}
